import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui";
import { useAppSelector } from "../../../hooks/useAppDispatch";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
import { useNavigate, useParams } from "react-router-dom";

const LOCALE = "modules_Guests_components_CartSection";

const CartSection: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Retrieve the gift table ID from the URL

  // Get the quantity in the cart from the Redux store
  const cartItems = useAppSelector((state) => state.guestCart.items);
  const cartQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );

  const handleScroll = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    timeoutId.current = setTimeout(() => {
      const currentScrollY = window.scrollY;
      setIsVisible(
        currentScrollY <= lastScrollY.current ||
          currentScrollY + window.innerHeight >= document.body.scrollHeight
      );
      lastScrollY.current = currentScrollY;
    }, 100);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  const handleCartClick = () => {
    if (id) {
      navigate(`/guests/gifttable/cart/${id}`);
    } else {
      console.error("Gift Table ID not found");
    }
  };

  return (
    <>
      {/* Desktop View */}
      <div className="hidden md:flex justify-end items-center p-4 md:p-4 bg-white space-x-8">
        <div className="text-right">
          <p className="text-sm text-gray-600">{t("cart.itemsLabel")}:</p>
          <p className="text-lg font-bold text-gray-900">{cartQuantity}</p>
        </div>
        <Button
          children={t("cart.viewButton")}
          onClick={handleCartClick}
          type="primary"
        />
      </div>

      {/* Mobile View (Only show if there are items in cart) */}
      {cartQuantity > 0 && (
        <div
          className={`md:hidden fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl bg-gray-100 p-4 text-left space-y-2 shadow-[0_-12px_30px_rgba(15,23,42,0.12)] transition-transform duration-300 ${
            isVisible ? "transform translate-y-0" : "transform translate-y-full"
          }`}
        >
          <div className="flex justify-between">
            <span className="text-gray-700">{t("cart.itemsLabel")}:</span>
            <span className="text-gray-900 font-bold">{cartQuantity}</span>
          </div>
          <div className="flex justify-center mt-2">
            <Button
              className="w-full"
              label={t("cart.viewButton")}
              onClick={handleCartClick}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default withTranslations(LOCALE)(CartSection);
